import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  CheckIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const ConfirmContext = createContext();

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default', // 'default', 'danger', 'warning', 'info'
    onConfirm: null,
    onCancel: null,
  });

  const confirm = useCallback(({
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'default',
  }) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        onConfirm: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  const danger = useCallback((title, message, confirmText = 'Delete') => {
    return confirm({ title, message, confirmText, variant: 'danger' });
  }, [confirm]);

  const warning = useCallback((title, message, confirmText = 'Proceed') => {
    return confirm({ title, message, confirmText, variant: 'warning' });
  }, [confirm]);

  const info = useCallback((title, message, confirmText = 'OK') => {
    return confirm({ title, message, confirmText, variant: 'info' });
  }, [confirm]);

  return (
    <ConfirmContext.Provider value={{ confirm, danger, warning, info }}>
      {children}
      <ConfirmDialog {...confirmState} />
    </ConfirmContext.Provider>
  );
};

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  variant,
  onConfirm,
  onCancel,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = (confirmed) => {
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      if (confirmed && onConfirm) {
        onConfirm();
      } else if (onCancel) {
        onCancel();
      }
    }, 200);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!shouldRender) return null;

  const variantConfig = {
    default: {
      icon: CheckIcon,
      iconBg: 'bg-gradient-to-br from-teal-500 to-emerald-600',
      iconColor: 'text-white',
      confirmBg: 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700',
      confirmText: 'text-white',
      ringColor: 'focus:ring-teal-500/30',
    },
    danger: {
      icon: TrashIcon,
      iconBg: 'bg-gradient-to-br from-rose-500 to-red-600',
      iconColor: 'text-white',
      confirmBg: 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700',
      confirmText: 'text-white',
      ringColor: 'focus:ring-rose-500/30',
    },
    warning: {
      icon: ExclamationTriangleIcon,
      iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
      iconColor: 'text-white',
      confirmBg: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
      confirmText: 'text-white',
      ringColor: 'focus:ring-amber-500/30',
    },
    info: {
      icon: InformationCircleIcon,
      iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      iconColor: 'text-white',
      confirmBg: 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
      confirmText: 'text-white',
      ringColor: 'focus:ring-blue-500/30',
    },
  };

  const config = variantConfig[variant] || variantConfig.default;
  const IconComponent = config.icon;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-message"
    >
      {/* Backdrop */}
      <div 
        className={`confirm-backdrop ${isClosing ? 'closing' : ''} fixed inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={() => handleClose(false)}
      />

      {/* Dialog Panel */}
      <div 
        className={`confirm-content ${isClosing ? 'closing' : ''} relative w-full max-w-[340px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden`}
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Content */}
        <div className="p-5">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`w-12 h-12 rounded-xl ${config.iconBg} flex items-center justify-center shadow-lg`}>
              <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
            </div>
          </div>

          {/* Title */}
          <h3 
            id="confirm-title"
            className="text-center text-base font-bold text-gray-900 dark:text-white mb-2"
          >
            {title}
          </h3>

          {/* Message */}
          <p 
            id="confirm-message"
            className="text-center text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
          >
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 pt-0">
          <button
            onClick={() => handleClose(false)}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400/30"
          >
            {cancelText}
          </button>
          <button
            onClick={() => handleClose(true)}
            autoFocus
            className={`flex-1 px-4 py-2.5 text-sm font-semibold ${config.confirmText} ${config.confirmBg} rounded-xl transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 ${config.ringColor} active:scale-[0.98]`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
